import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/useLocale";
import type { TFunction } from "i18next";
import { ViewAction } from "@/components/table/actions";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Layers, UserIcon } from "lucide-react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table } from "@/components/table";
import { useGetWorkflowVersionsQuery } from "@/services/(company)/workflowApi";
import { usePageTitle } from "@/hooks/usePageTitle";
import { withBranding } from "@/utils/helper";
import { PATHS } from "@/routes/paths";
import { COMPANY_STUDIO_PATHS } from "@/routes/company/routes.studio";
import { getCategoryForModule } from "@/pages/(home)/(company)/studio/workflows/_components/workflow-types";

const buildColumns = (t: TFunction) => [
  { key: "version", label: t("company:workflows.col_version") },
  { key: "publishedBy", label: t("company:workflows.col_published_by") },
  { key: "publishedAt", label: t("company:workflows.col_published_at") },
  { key: "instances", label: t("company:workflows.col_instances") },
  { key: "status", label: t("company:workflows.col_status") },
  { key: "actions", label: t("company:workflows.col_actions") },
];

const WorkflowVersionsList = () => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate } = useLocale();
  const columns = buildColumns(t);
  usePageTitle(withBranding(t("company:workflows.page_title_versions")));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isFetching } = useGetWorkflowVersionsQuery(id!, {
    skip: !id,
  });

  const template = data?.meta?.template;
  const versions = data?.data ?? [];

  // The Studio builder shows the live template (version snapshots aren't
  // rendered there yet); open the workflow in Studio.
  const buildViewHref = () =>
    COMPANY_STUDIO_PATHS.WORKFLOW_BUILDER(
      getCategoryForModule(template?.module ?? ""),
      id!,
    );

  const buildInstancesHref = (versionId: number) =>
    PATHS.COMPANY.COMPANY_WORKFLOWS_PATHS.INSTANCE_WITH(id!, { versionId });

  const rows = versions.map((v) => ({
    versionId: v.id,
    version: (
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-800">
          {t("company:workflows.version_short", { number: v.versionNumber })}
        </span>
        <span className="text-xs text-gray-500 truncate max-w-[280px]">
          {v.name}
        </span>
      </div>
    ),
    publishedBy: v.publishedBy ? (
      <div className="flex items-center gap-2">
        <Avatar className="size-9 bg-primary/10 border border-white shrink-0">
          <AvatarImage
            src={v.publishedBy.profileImage ?? ""}
            alt={v.publishedBy.firstName}
          />
          <AvatarFallback>
            <UserIcon size={14} className="text-white" />
          </AvatarFallback>
        </Avatar>
        <span className="text-xs text-gray-800">
          {v.publishedBy.firstName} {v.publishedBy.lastName}
        </span>
      </div>
    ) : (
      <span className="text-xs text-gray-400">—</span>
    ),
    publishedAt: (
      <span className="text-xs text-gray-700">
        {v.publishedAt
          ? formatDate(new Date(v.publishedAt), "dd MMM yyyy, hh:mm a")
          : "-"}
      </span>
    ),
    instances: (
      <span className="text-xs text-gray-700">
        {t("company:workflows.instance_count", { count: v.instanceCount })}
      </span>
    ),
    status: v.isLatest ? (
      <Badge className="bg-green-100 text-green-700 font-medium">
        {t("company:workflows.status_latest")}
      </Badge>
    ) : (
      <Badge className="bg-gray-100 text-gray-600 font-medium">
        {t("company:workflows.status_archived")}
      </Badge>
    ),
    actions: (
      <div
        className="flex gap-2 items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <Link to={buildViewHref()}>
          <ViewAction tooltip={t("company:workflows.view_this_version")} />
        </Link>
        <Link
          to={buildInstancesHref(v.id)}
          onClick={(e) => v.instanceCount === 0 && e.preventDefault()}
          tabIndex={v.instanceCount === 0 ? -1 : undefined}
        >
          <Button
            variant="ghost"
            size="icon"
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={
              v.instanceCount > 0
                ? t("company:workflows.view_instances_n", {
                    count: v.instanceCount,
                  })
                : t("company:workflows.view_instances")
            }
            className="relative"
            disabled={v.instanceCount === 0}
          >
            <Layers className="w-4 h-4 text-info" />
            {v.instanceCount > 0 && (
              <span className="absolute top-0.5 end-1 min-w-[14px] h-[14px] px-[3px] rounded-full bg-info text-white text-[8px] font-bold leading-[14px] text-center">
                {v.instanceCount > 99 ? "99+" : v.instanceCount}
              </span>
            )}
          </Button>
        </Link>
      </div>
    ),
  }));

  return (
    <Card className="border-none shadow-none">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3 px-6 pt-6 pb-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="h-9 w-9 flex items-center justify-center rounded-xs border border-border shrink-0 hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={18} className="text-primary rtl:rotate-180" />
          </button>
          <CardTitle className="text-lg font-semibold">
            {template?.name ?? t("company:workflows.page_title_versions")}
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="px-0">
        <Table
          columns={columns}
          data={rows}
          loading={isLoading || isFetching}
          onRowClick={() => navigate(buildViewHref())}
        />
      </CardContent>
    </Card>
  );
};

export default WorkflowVersionsList;
